
import React, { useMemo } from 'react';
import { GoogleGenAI } from "@google/genai";
import { UserRole, ScheduleEntry, AgendaEntry, Student, View, ActivityType, AttendanceRecord, AssessmentGrade } from '../types'; // Import AttendanceRecord and AssessmentGrade
import { 
  Users, 
  BookOpen, 
  CheckCircle, 
  TrendingUp,
  AlertCircle, 
  Calendar, 
  ShieldCheck, 
  GraduationCap, 
  Clock,
  BookMarked,
  ChevronDown,
  UserCheck,
  Sparkles,
  ClipboardList,
  PenTool,
  FileDown,
  ArrowRight,
  Search,
  User,
  Eye,
  EyeOff,
  X,
  Building2,
  CalendarClock,
  Coffee,
  Timer,
  Palette,
  Layers,
  Sparkle
} from 'lucide-react';
import { APP_THEMES, getThemeById, GlobalTheme } from '../src/themeConfig.ts';

interface DashboardProps {
  role: UserRole;
  activeUser: any;
  schedules: ScheduleEntry[];
  agendas: AgendaEntry[];
  students: Student[];
  teachers?: any[];
  onNavigate: (view: View, params?: any) => void;
  onUpdateAgenda?: (updated: AgendaEntry) => void;
  schoolSettings: any;
  onUpdateSettings?: (settings: any) => void;
  assessmentGrades: AssessmentGrade[];
  attendanceRecords: AttendanceRecord[];
  onUpdateTeacher?: (updated: any) => void;
  isDarkMode?: boolean;
  appTheme?: string;
  onNextTheme?: () => void;
  onSelectTheme?: (themeId: string) => void;
}

const StatCard: React.FC<{ title: string; value: string; sub: string; icon: React.ReactNode, color: string, onClick?: () => void }> = ({ title, value, sub, icon, color, onClick }) => (
  <div 
    onClick={onClick}
    className={`p-5 sm:p-6 ${color} rounded-3xl sm:rounded-[2.5rem] shadow-xl shadow-black/5 border border-white/10 dark:border-white/5 flex flex-col gap-3 sm:gap-5 hover:shadow-2xl hover:shadow-black/10 hover:-translate-y-1 transition-all duration-300 group overflow-hidden relative ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div className={`absolute top-0 right-0 w-16 h-16 sm:w-24 sm:h-24 bg-white opacity-10 rounded-full -mr-8 -mt-8 sm:-mr-12 sm:-mt-12 group-hover:scale-150 transition-transform duration-500`}></div>
    <div className="flex justify-between items-start relative z-10">
        <div className="p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl bg-white/20 text-white group-hover:scale-110 transition-transform">
            {icon}
        </div>
        <span className="text-[9px] sm:text-[10px] font-black text-white bg-white/20 px-2 sm:px-2.5 py-1 rounded-lg uppercase tracking-widest">{sub}</span>
    </div>
    <div className="relative z-10">
      <p className="text-[10px] sm:text-xs font-bold text-white mb-0.5 sm:mb-1 tracking-tight">{title}</p>
      <p className="text-2xl sm:text-4xl font-black text-white tracking-tight">{value}</p>
    </div>
  </div>
);

const DashboardView: React.FC<DashboardProps> = ({ 
  role, 
  activeUser, 
  schedules, 
  agendas, 
  students, 
  teachers = [], 
  onNavigate, 
  onUpdateAgenda, 
  schoolSettings, 
  onUpdateSettings, 
  assessmentGrades, 
  attendanceRecords, 
  onUpdateTeacher, 
  isDarkMode,
  appTheme = 'wzc-themes',
  onNextTheme,
  onSelectTheme
}) => {
  const [currentTime, setCurrentTime] = React.useState(new Date());

  React.useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const currentGlobalTheme = useMemo(() => getThemeById(appTheme), [appTheme]);
  const activeTheme = currentGlobalTheme;

  const todayStr = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }, []);
  const dayNames = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
  const todayDayName = dayNames[new Date().getDay()];

  const [dismissedAnnouncements, setDismissedAnnouncements] = React.useState<string[]>(() => {
    const saved = localStorage.getItem('smpn1_dismissed_announcements');
    return saved ? JSON.parse(saved) : [];
  });

  const handleDismissAnnouncement = (id: string) => {
    const updated = [...dismissedAnnouncements, id];
    setDismissedAnnouncements(updated);
    localStorage.setItem('smpn1_dismissed_announcements', JSON.stringify(updated));
  };

  const handleDeleteAnnouncement = (id: string) => {
    if (onUpdateSettings && schoolSettings.announcements) {
      const updated = (schoolSettings.announcements || []).filter((a: any) => a.id !== id);
      onUpdateSettings({ ...schoolSettings, announcements: updated });
    }
  };

  const visibleAnnouncements = useMemo(() => {
    return (schoolSettings.announcements || []).filter((ann: any) => !dismissedAnnouncements.includes(ann.id));
  }, [schoolSettings.announcements, dismissedAnnouncements]);

  const isMatchingAcademicYear = (itemYear?: string, targetYear?: string) => {
    if (!targetYear || !itemYear) return true;
    return itemYear.trim().toLowerCase() === targetYear.trim().toLowerCase();
  };

  const isMatchingSemester = (itemSem?: string, targetSem?: string) => {
    if (!targetSem || !itemSem) return true;
    const normItem = itemSem.toString().toLowerCase();
    const normTarget = targetSem.toString().toLowerCase();
    
    const itemIs1 = normItem.includes('1') || normItem.includes('ganjil');
    const itemIs2 = normItem.includes('2') || normItem.includes('genap');
    const targetIs1 = normTarget.includes('1') || normTarget.includes('ganjil');
    const targetIs2 = normTarget.includes('2') || normTarget.includes('genap');
    
    if (itemIs1 && targetIs1) return true;
    if (itemIs2 && targetIs2) return true;
    return normItem === normTarget;
  };

  const currentDayTimes = useMemo(() => {
    return schoolSettings.dayPeriodTimes?.[todayDayName] || schoolSettings.periodTimes;
  }, [schoolSettings, todayDayName]);

  const todaySchedules = useMemo(() => {
    return (schedules || []).filter(s => s.day === todayDayName).sort((a, b) => a.period - b.period);
  }, [schedules, todayDayName]);

  const todayBreaks = useMemo(() => {
    const breakEntries = (todaySchedules || []).filter(s => s.isBreak || s.subject === 'Jam Istirahat' || s.class === 'Istirahat');
    return breakEntries.map(s => ({
      startPeriod: s.period,
      endPeriod: s.period,
      isBreak: true as const,
      subject: 'Jam Istirahat',
      class: 'Istirahat',
    }));
  }, [todaySchedules]);

  const todayTeacherAgendas = useMemo(() => {
    const activeAcademicYear = schoolSettings?.academicYear;
    const activeSemester = schoolSettings?.semester;
    const teacherSubject = (activeUser?.subject || '').trim().toLowerCase();
    const assignedClassesNormalized = (activeUser?.assignedClasses || []).map((c: string) => c.trim().toLowerCase());

    return (agendas || []).filter(a => {
      if (a.isArchived) return false;
      if (a.date !== todayStr) return false;

      const agTeacherId = a.teacherId;
      if (agTeacherId && activeUser?.id) {
        if (agTeacherId !== activeUser.id) return false;
      } else {
        const agClass = (a.class || '').trim().toLowerCase();
        const agSubject = (a.subject || '').trim().toLowerCase();
        const isSubjectMatch = teacherSubject && agSubject && agSubject === teacherSubject;
        const isClassMatch = assignedClassesNormalized.length > 0 && agClass && (assignedClassesNormalized.includes(agClass) || agClass === 'semua kelas');
        const isExtraMatch = (a.isExtra || a.startPeriod === 0) && (isSubjectMatch || !a.class);
        if (!isSubjectMatch && !isClassMatch && !isExtraMatch) return false;
      }

      return isMatchingAcademicYear(a.academicYear, activeAcademicYear) &&
             isMatchingSemester(a.semester, activeSemester);
    });
  }, [agendas, todayStr, activeUser, schoolSettings]);

  const teacherTimelineItems = useMemo(() => {
    const breaksFormatted = todayBreaks.map(b => ({
      type: 'break' as const,
      startPeriod: b.startPeriod,
      endPeriod: b.endPeriod,
      isBreak: true,
      data: b,
    }));

    const isPeriodABreak = (p: number) => todayBreaks.some(b => Math.abs(b.startPeriod - p) < 0.01 || (p >= b.startPeriod && p <= b.endPeriod));
    const hasBreakAfterPeriod = (p: number) => todayBreaks.some(b => b.startPeriod > p && b.startPeriod < p + 1);

    const agendasFormatted: any[] = [];

    todayTeacherAgendas.forEach(a => {
      const rawStart = typeof a.startPeriod === 'number' ? a.startPeriod : 1;
      const rawEnd = typeof a.endPeriod === 'number' ? a.endPeriod : rawStart;

      // Handle Jam ke-0 / Agenda Tambahan tanpa rentang reguler
      if (rawStart === 0 && rawEnd === 0) {
        agendasFormatted.push({
          type: 'agenda' as const,
          startPeriod: 0,
          endPeriod: 0,
          isBreak: false,
          data: a,
        });
        return;
      }

      let subStart: number | null = null;
      let subEnd: number | null = null;

      for (let p = rawStart; p <= rawEnd; p++) {
        if (isPeriodABreak(p)) {
          // Jam pelajaran saat ini adalah jam istirahat
          if (subStart !== null && subEnd !== null) {
            agendasFormatted.push({
              type: 'agenda' as const,
              startPeriod: subStart,
              endPeriod: subEnd,
              isBreak: false,
              data: a,
            });
            subStart = null;
            subEnd = null;
          }
        } else {
          if (subStart === null) {
            subStart = p;
            subEnd = p;
          } else {
            subEnd = p;
          }

          // Cek jika ada jeda istirahat di antara jam p dan p + 1 (misal 4.5)
          if (p < rawEnd && hasBreakAfterPeriod(p)) {
            agendasFormatted.push({
              type: 'agenda' as const,
              startPeriod: subStart,
              endPeriod: subEnd,
              isBreak: false,
              data: a,
            });
            subStart = null;
            subEnd = null;
          }
        }
      }

      if (subStart !== null && subEnd !== null) {
        agendasFormatted.push({
          type: 'agenda' as const,
          startPeriod: subStart,
          endPeriod: subEnd,
          isBreak: false,
          data: a,
        });
      }
    });

    return [...breaksFormatted, ...agendasFormatted].sort((x, y) => x.startPeriod - y.startPeriod);
  }, [todayBreaks, todayTeacherAgendas]);

  const parseTimeToMinutes = (timeStr?: string) => {
    if (!timeStr) return null;
    const clean = timeStr.trim().replace('.', ':');
    const parts = clean.split(':');
    if (parts.length < 2) return null;
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    if (isNaN(hours) || isNaN(minutes)) return null;
    return hours * 60 + minutes;
  };

  const formatMinutesToTime = (totalMinutes: number): string => {
    const h = Math.floor(totalMinutes / 60) % 24;
    const m = totalMinutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const getItemTimeRange = (item: { startPeriod: number, endPeriod: number, isBreak?: boolean }) => {
    if (!currentDayTimes || currentDayTimes.length === 0) return { startTimeStr: undefined, endTimeStr: undefined };

    let startTimeStr: string | undefined;
    let endTimeStr: string | undefined;

    if (item.isBreak) {
      const baseP = Math.floor(item.startPeriod);
      const prevPt = currentDayTimes.find((pt: any) => pt.period === baseP);
      const nextPt = currentDayTimes.find((pt: any) => pt.period === baseP + 1);
      const exactPt = currentDayTimes.find((pt: any) => pt.period === item.startPeriod);

      if (exactPt?.startTime && exactPt?.endTime) {
        startTimeStr = exactPt.startTime;
        endTimeStr = exactPt.endTime;
      } else if (prevPt?.endTime && nextPt?.startTime) {
        startTimeStr = prevPt.endTime;
        endTimeStr = nextPt.startTime;
      }
    } else {
      if (item.startPeriod === 0 && item.endPeriod === 0) {
        // Jam ke-0 setiap hari mulainya pukul 07:00
        startTimeStr = '07:00';
        const pt1 = currentDayTimes.find((pt: any) => pt.period === 1);
        const pt1Min = pt1?.startTime ? parseTimeToMinutes(pt1.startTime) : null;
        if (pt1Min !== null && pt1Min > 420) {
          endTimeStr = pt1.startTime;
        } else {
          const pt0 = currentDayTimes.find((pt: any) => pt.period === 0);
          endTimeStr = pt0?.endTime || '07:30';
        }
      } else {
        const startPt = currentDayTimes.find((pt: any) => pt.period === item.startPeriod);
        const endPt = currentDayTimes.find((pt: any) => pt.period === item.endPeriod);
        startTimeStr = startPt?.startTime;
        endTimeStr = endPt?.endTime;
      }
    }

    return { startTimeStr, endTimeStr };
  };

  const isItemActive = (item: { startPeriod: number, endPeriod: number, isBreak?: boolean }) => {
    const { startTimeStr, endTimeStr } = getItemTimeRange(item);
    if (!startTimeStr || !endTimeStr) return false;

    const startMin = parseTimeToMinutes(startTimeStr);
    const endMin = parseTimeToMinutes(endTimeStr);
    if (startMin === null || endMin === null) return false;

    const currentMin = currentTime.getHours() * 60 + currentTime.getMinutes();
    return currentMin >= startMin && currentMin < endMin;
  };

  const getItemEndTime = (item: { startPeriod: number, endPeriod: number, isBreak?: boolean }) => {
    const { endTimeStr } = getItemTimeRange(item);
    if (!endTimeStr) return null;
    const clean = endTimeStr.trim().replace('.', ':');
    const parts = clean.split(':');
    if (parts.length < 2) return null;
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    if (isNaN(hours) || isNaN(minutes)) return null;

    const targetDate = new Date(currentTime);
    targetDate.setHours(hours, minutes, 0, 0);
    return targetDate;
  };

  const getItemRemainingText = (item: { startPeriod: number, endPeriod: number, isBreak?: boolean }) => {
    const targetDate = getItemEndTime(item);
    if (!targetDate) return null;

    const diffMs = targetDate.getTime() - currentTime.getTime();
    if (diffMs <= 0) return '0 mnt (Akan Berakhir)';

    const diffTotalSec = Math.floor(diffMs / 1000);
    const hours = Math.floor(diffTotalSec / 3600);
    const minutes = Math.floor((diffTotalSec % 3600) / 60);
    const seconds = diffTotalSec % 60;

    if (hours > 0) {
      return `${hours} jam ${minutes} mnt ${seconds} dtk`;
    } else if (minutes > 0) {
      return `${minutes} mnt ${seconds} dtk`;
    } else {
      return `${seconds} dtk`;
    }
  };

  const stats = useMemo(() => {
    const activeAcademicYear = schoolSettings?.academicYear;
    const activeSemester = schoolSettings?.semester;

    if (role === UserRole.Admin) {
      const activeAdminStudentsCount = (students || []).filter(s => (s.status || 'Aktif') === 'Aktif').length;
      const activeAdminAgendasCount = (agendas || []).filter(a => 
        !a.isArchived && 
        isMatchingAcademicYear(a.academicYear, activeAcademicYear) && 
        isMatchingSemester(a.semester, activeSemester)
      ).length;

      return [
        { title: 'Total Guru SMP', value: String(teachers.length), sub: 'Aktif', icon: <GraduationCap size={24} />, color: 'bg-indigo-600 dark:bg-indigo-700' },
        { title: 'Data Siswa', value: String(activeAdminStudentsCount), sub: activeAcademicYear ? `Tahun Ajaran ${activeAcademicYear}` : 'Aktif', icon: <Users size={24} />, color: 'bg-cyan-500 dark:bg-cyan-600' },
        { title: 'Total Kelas', value: String(schoolSettings?.classes?.length || 0), sub: 'Rombel', icon: <BookOpen size={24} />, color: 'bg-amber-500 dark:bg-amber-600' },
        { title: 'Total Agenda', value: String(activeAdminAgendasCount), sub: activeSemester ? `Sem ${activeSemester}` : 'Semua', icon: <Calendar size={24} />, color: 'bg-green-500 dark:bg-green-600' },
      ];
    }
    
    // Teacher stats
    const teacherSubject = (activeUser?.subject || '').trim().toLowerCase();
    const assignedClassesNormalized = (activeUser?.assignedClasses || []).map((c: string) => c.trim().toLowerCase());

    // 1. Siswa Mengajar (Filter hanya siswa aktif)
    const teacherStudents = (students || []).filter(s => {
      const isClassMatch = assignedClassesNormalized.includes((s.class || '').trim().toLowerCase());
      const isActiveStatus = (s.status || 'Aktif') === 'Aktif';
      return isClassMatch && isActiveStatus;
    });

    // 2. Total Agenda (Akurat sesuai Agenda Harian - Rekap Agenda)
    const teacherAgendas = (agendas || []).filter(a => {
      if (a.isArchived) return false;

      const agClass = (a.class || '').trim().toLowerCase();
      const agSubject = (a.subject || '').trim().toLowerCase();
      const agTeacherId = a.teacherId;

      if (agTeacherId && activeUser?.id) {
        return agTeacherId === activeUser.id;
      }

      // Fallback for legacy agendas without teacherId
      const isSubjectMatch = teacherSubject && agSubject && agSubject === teacherSubject;
      const isClassMatch = assignedClassesNormalized.length > 0 && agClass && (assignedClassesNormalized.includes(agClass) || agClass === 'semua kelas');

      return isSubjectMatch && isClassMatch;
    });

    // 3. Total Penilaian (Filter kelas mengajar & semester/tahun ajaran aktif)
    const teacherGrades = (assessmentGrades || []).filter(g => {
      const gClass = (g.class || '').trim().toLowerCase();
      const isClassMatch = gClass === 'semua kelas' || assignedClassesNormalized.includes(gClass);
      if (!isClassMatch) return false;

      const matchYear = isMatchingAcademicYear(g.academicYear, activeAcademicYear);
      const matchSem = isMatchingSemester(g.semester, activeSemester);

      return matchYear && matchSem;
    });
    
    // 4. Absensi Belum (Filter berdasarkan agenda semester/tahun ajaran aktif)
    const pendingAgendas = (teacherAgendas || []).filter(a => {
      // Exclude extra agendas without a class (SUMATIF/SAS)
      const agClass = (a.class || '').trim().toLowerCase();
      const isExtraAgendaWithoutClass = (a.activityType === ActivityType.SUMATIF || a.activityType === ActivityType.SAS || (a.activityType as any) === 'SAS' || (a.activityType as any) === 'Sumatif Akhir') && (!agClass || agClass === '');
      // Exclude 'Jam kosong' status
      const isJamKosong = a.status === 'Jam kosong';
      // Exclude activities that do not require attendance
      const isNoAbsenceActivity = [
        ActivityType.KEGIATAN_LOMBA, 
        ActivityType.KEGIATAN_KESISWAAN, 
        ActivityType.KEGIATAN_SEKOLAH, 
        ActivityType.SAS
      ].includes(a.activityType as any) || 
      (a.activityType as any) === 'SAS' || 
      (a.activityType as any) === 'Sumatif Akhir' ||
      a.isAttendanceOptional;
      
      if (isExtraAgendaWithoutClass || isJamKosong || isNoAbsenceActivity) return false;

      const classStudents = (students || []).filter(s => (s.class || '').trim().toLowerCase() === agClass && (s.status || 'Aktif') === 'Aktif');
      const recordCount = (attendanceRecords || []).filter(r => r.agendaId === a.id).length;
      
      // Attendance is pending if record count is less than active student count
      return recordCount < classStudents.length && classStudents.length > 0;
    });
    const absensiBelum = pendingAgendas.length;

    const handleNavigate = (view: View, params?: any) => {
      onNavigate(view, params);
    };

    return [
      { title: 'Siswa Mengajar', value: String(teacherStudents.length), sub: 'Siswa', icon: <Users size={24} />, color: 'bg-sky-500 dark:bg-sky-600', onClick: () => handleNavigate(View.StudentList) },
      { title: 'Total Agenda', value: String(teacherAgendas.length), sub: 'Semua', icon: <BookOpen size={24} />, color: 'bg-pink-500 dark:bg-pink-600', onClick: () => handleNavigate(View.Agenda, { tab: 'recap' }) },
      { title: 'Total Penilaian', value: String(teacherGrades.length), sub: 'Data', icon: <ClipboardList size={24} />, color: 'bg-teal-500 dark:bg-teal-600', onClick: () => handleNavigate(View.Grading) },
      { title: 'Absensi Belum', value: String(absensiBelum), sub: 'Agenda', icon: <AlertCircle size={24} />, color: 'bg-orange-500 dark:bg-orange-600', onClick: () => handleNavigate(View.Attendance, { tab: 'pending' }) },
    ];
  }, [role, students, teachers.length, agendas, todayStr, teacherTimelineItems, activeUser, assessmentGrades, schoolSettings, attendanceRecords, onNavigate]);

  const agendasInProgress = useMemo(() => {
    const assignedClasses = activeUser?.assignedClasses || [];
    const teacherSubject = activeUser?.subject || '';
    const activeAcademicYear = schoolSettings?.academicYear;
    const activeSemester = schoolSettings?.semester;

    const teacherAgendas = role === UserRole.Admin 
      ? (agendas || []) 
      : (agendas || []).filter(a => {
          if (a.isArchived) return false;
          const isClassOrSubject = assignedClasses.includes(a.class) || 
            (a.startPeriod === 0 && a.subject === teacherSubject);
          if (!isClassOrSubject) return false;

          return isMatchingAcademicYear(a.academicYear, activeAcademicYear) &&
                 isMatchingSemester(a.semester, activeSemester);
        });
    return (teacherAgendas || [])
      .filter(a => a.status === 'Belum Selesai')
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [agendas, activeUser, role, schoolSettings]);

  const [searchQuery, setSearchQuery] = React.useState('');
  
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return (students || []).filter(s => {
      const name = (s.name || '').toLowerCase();
      const nis = (s.nis || '').toLowerCase();
      const nisn = (s.nisn || '').toLowerCase();
      return name.includes(query) || nis.includes(query) || nisn.includes(query);
    }).slice(0, 5);
  }, [students, searchQuery]);

  const handleStatusToggle = (agenda: AgendaEntry, newStatus: AgendaEntry['status']) => {
    if (onUpdateAgenda) {
      onUpdateAgenda({ ...agenda, status: newStatus });
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in pb-10">
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 sm:gap-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-500 to-pink-500 dark:from-indigo-400 dark:via-purple-400 dark:to-pink-400 tracking-tight leading-tight">
            {role === UserRole.Admin ? 'Dashboard Super Admin 🖥️' : `Dashboard Guru 👨‍🏫`}
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium mt-1">Sistem Administrasi SMPN 1 Kawali</p>
        </div>

        <div className="w-full xl:w-auto flex flex-wrap sm:flex-nowrap items-center gap-3 shrink-0">
          {/* Tanggal & Jam */}
          <div className="flex-1 sm:flex-initial flex items-center justify-between sm:justify-start gap-3 bg-white dark:bg-slate-900 px-4 py-3 sm:px-5 sm:py-3 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800">
            <div className="flex items-center gap-2">
              <Calendar className="text-indigo-600 shrink-0" size={18} />
              <span className="text-xs sm:text-sm font-black text-slate-800 dark:text-white capitalize">
                {currentTime.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            </div>
            <div className="h-4 w-px bg-slate-200 dark:bg-slate-700"></div>
            <div className="flex items-center gap-1.5 text-xs sm:text-sm font-black text-indigo-600 dark:text-indigo-400">
              <Clock size={16} className="shrink-0" />
              <span>{currentTime.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
            </div>
          </div>

          {/* Periode Akademik Aktif */}
          <div className="flex-1 sm:flex-initial flex items-center gap-3 bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-900 dark:to-purple-900 px-4 py-3 sm:px-5 sm:py-3 rounded-2xl shadow-sm border border-indigo-500/20 text-white">
            <div className="p-1.5 bg-white/20 rounded-xl backdrop-blur-sm shrink-0">
              <CalendarClock size={16} className="text-white" />
            </div>
            <div className="flex items-center gap-3">
              <div>
                <span className="text-[9px] font-bold text-indigo-200 uppercase tracking-wider block leading-none mb-0.5">Tahun Ajaran</span>
                <span className="text-xs sm:text-sm font-black text-white leading-none">{schoolSettings?.academicYear || '2025/2026'}</span>
              </div>
              <div className="h-4 w-px bg-white/20 my-auto"></div>
              <div>
                <span className="text-[9px] font-bold text-indigo-200 uppercase tracking-wider block leading-none mb-0.5">Semester</span>
                <span className="text-xs sm:text-sm font-black text-white leading-none">{schoolSettings?.semester || '2 (Genap)'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
        <div className="lg:col-span-2 space-y-6 sm:space-y-8">
            <div className="bg-white dark:bg-slate-900 p-5 sm:p-8 rounded-3xl sm:rounded-[2.5rem] shadow-sm border border-slate-200 dark:border-slate-800">
            {role === UserRole.Siswa ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-2 sm:p-4">
                <div 
                  onClick={() => onNavigate(View.StudentProfile)}
                  className="p-8 text-center bg-indigo-50 dark:bg-indigo-900/20 rounded-[2rem] border border-indigo-100 dark:border-indigo-800 hover:scale-[1.02] transition-all cursor-pointer group"
                >
                  <User size={48} className="mx-auto text-indigo-600 mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-black mb-2 text-slate-800 dark:text-white">Profil & Presensi</h3>
                  <p className="text-slate-500 mb-6 text-sm font-bold">Lihat Presensi, Riwayat Pertemuan & Info Saya</p>
                  <div className="inline-flex px-6 py-2 bg-indigo-600 text-white font-black rounded-xl uppercase text-[10px] tracking-widest shadow-lg shadow-indigo-500/30">
                    Buka Profil
                  </div>
                </div>
                <div 
                  onClick={() => onNavigate(View.NilaiRapor)}
                  className="p-8 text-center bg-amber-50 dark:bg-amber-900/20 rounded-[2rem] border border-amber-100 dark:border-amber-800 hover:scale-[1.02] transition-all cursor-pointer group"
                >
                  <GraduationCap size={48} className="mx-auto text-amber-600 mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-black mb-2 text-slate-800 dark:text-white">Nilai Rapor</h3>
                  <p className="text-slate-500 mb-6 text-sm font-bold">Lihat capaian hasil belajar Anda</p>
                  <div className="inline-flex px-6 py-2 bg-amber-500 text-white font-black rounded-xl uppercase text-[10px] tracking-widest shadow-lg shadow-amber-500/30">
                    Buka Rapor
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Status Sistem/Maintenance Toggle - Quick Access for Guru & Admin */}
                <div 
                  onClick={() => onUpdateSettings && onUpdateSettings({ ...schoolSettings, isStudentMaintenanceMode: !schoolSettings.isStudentMaintenanceMode })}
                  className={`mb-6 p-4 rounded-2xl border-2 cursor-pointer transition-all flex items-center justify-between ${
                    schoolSettings.isStudentMaintenanceMode 
                      ? 'bg-amber-50 border-amber-500 dark:bg-amber-500/10' 
                      : 'bg-slate-50 border-transparent dark:bg-slate-800/40 hover:border-slate-200 dark:hover:border-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${schoolSettings.isStudentMaintenanceMode ? 'bg-amber-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-500'}`}>
                      <AlertCircle size={16} />
                    </div>
                    <div>
                      <h4 className="text-xs font-black text-slate-900 dark:text-white leading-none">Akses Dashboard Siswa</h4>
                      <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium mt-1">
                        {schoolSettings.isStudentMaintenanceMode ? 'Siswa sedang dikunci (Maintenance Mode)' : 'Halaman Siswa Aktif / Normal'}
                      </p>
                    </div>
                  </div>
                  <div className={`w-10 h-5 rounded-full relative transition-colors ${schoolSettings.isStudentMaintenanceMode ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-600'}`}>
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-all ${schoolSettings.isStudentMaintenanceMode ? 'right-0.5' : 'left-0.5'}`}></div>
                  </div>
                </div>

                {role === UserRole.Admin ? (
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2.5 sm:p-3 bg-indigo-600 text-white rounded-xl sm:rounded-2xl shadow-lg shadow-indigo-500/20">
                            <ShieldCheck size={18} className="sm:w-5 sm:h-5" />
                        </div>
                        <div>
                            <h3 className="text-lg sm:text-xl font-black text-slate-900 dark:text-white tracking-tight">Kontrol Admin</h3>
                            <p className="text-[9px] sm:text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5 sm:mt-1">PENGATURAN GLOBAL SEKOLAH</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <button 
                        onClick={() => onNavigate(View.Settings)}
                        className="flex items-center justify-center gap-2 p-4 bg-slate-50 dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-2xl border border-slate-100 dark:border-slate-700 transition-all group"
                      >
                        <Building2 size={16} className="text-indigo-600" />
                        <span className="text-[10px] font-black uppercase tracking-widest">Profil Sekolah</span>
                      </button>
                      <button 
                        onClick={() => onNavigate(View.PeriodSettings)}
                        className="flex items-center justify-center gap-2 p-4 bg-slate-50 dark:bg-slate-800 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-2xl border border-slate-100 dark:border-slate-700 transition-all group"
                      >
                        <CalendarClock size={16} className="text-amber-500" />
                        <span className="text-[10px] font-black uppercase tracking-widest">Jam Belajar</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
                        <div className="flex items-center gap-2.5">
                            <div className="p-2 sm:p-2.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-xl border border-indigo-100 dark:border-indigo-900/60 shadow-xs">
                                <CalendarClock size={18} />
                            </div>
                            <div>
                                <h3 className="text-base sm:text-lg font-black text-slate-900 dark:text-white tracking-tight">Jadwal & Presensi Hari Ini</h3>
                                <p className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest">AGENDA KBM & KEGIATAN AKTIF</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Theme switcher: Next Theme */}
                          <button
                            onClick={onNextTheme}
                            className="group flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 text-white text-xs font-black shadow-md shadow-purple-500/20 active:scale-95 transition-all cursor-pointer"
                            title="Ganti tema untuk seluruh aplikasi (5 Tema Global)"
                          >
                            <Palette size={14} className="group-hover:rotate-45 transition-transform duration-300" />
                            <span>Next Theme</span>
                            <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-md bg-white/20 backdrop-blur-sm border border-white/25">
                              {currentGlobalTheme.tag}: {currentGlobalTheme.shortName}
                            </span>
                          </button>

                          {teacherTimelineItems.length > 0 && (
                            <span className="text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200/70 dark:border-slate-700/60">
                              {teacherTimelineItems.length} Sesi
                            </span>
                          )}
                        </div>
                    </div>

                    <div className="space-y-3.5 sm:space-y-4">
                        {teacherTimelineItems.length > 0 ? teacherTimelineItems.map((item, idx) => {
                            const isCurrentActive = isItemActive(item);
                            const { startTimeStr, endTimeStr } = getItemTimeRange(item);
                            const remainingText = isCurrentActive ? getItemRemainingText(item) : null;

                            if (item.type === 'break') {
                              return (
                                <div 
                                  key={`break-item-${idx}-${item.startPeriod}`} 
                                  className={`relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 p-4 sm:p-5 rounded-2xl sm:rounded-3xl border transition-all duration-300 overflow-hidden ${activeTheme.breakCard} ${
                                    isCurrentActive ? activeTheme.activeRing : 'hover:scale-[1.005]'
                                  }`}
                                >
                                  <div className="flex items-center gap-3.5 sm:gap-4 z-10 w-full sm:w-auto">
                                    <div className={`p-2.5 sm:p-3 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0 ${activeTheme.breakIcon}`}>
                                      <Coffee size={22} className={isCurrentActive ? 'animate-bounce' : ''} />
                                    </div>
                                    <div className="min-w-0">
                                      <div className="flex flex-wrap items-center gap-2 mb-1">
                                        <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-white/20 border border-white/25 inline-flex items-center gap-1.5 text-white">
                                          Istirahat
                                          {isCurrentActive && (
                                            <span className="w-2 h-2 rounded-full bg-amber-300 animate-ping" />
                                          )}
                                        </span>
                                        {isCurrentActive && remainingText && (
                                          <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-wider bg-amber-300 text-slate-950 shadow-md border border-amber-200">
                                            <Timer size={12} className="animate-spin text-slate-900" />
                                            <span>Habis dalam {remainingText}</span>
                                          </span>
                                        )}
                                      </div>
                                      <h4 className="text-base sm:text-lg font-black text-white tracking-tight flex items-center gap-2">
                                        <span>☕ Jam Istirahat</span>
                                        {isCurrentActive && (
                                          <span className="text-[9px] sm:text-[10px] font-black px-2.5 py-0.5 rounded-full bg-white text-emerald-800 uppercase tracking-wider shadow-sm animate-pulse">
                                            Sedang Berlangsung
                                          </span>
                                        )}
                                      </h4>
                                      <p className="text-xs sm:text-sm font-semibold text-white/90 flex items-center gap-1.5 mt-0.5">
                                        <Clock size={13} /> {startTimeStr && endTimeStr ? `Pukul ${startTimeStr} - ${endTimeStr}` : `Jam Ke-${item.startPeriod}`}
                                      </p>
                                    </div>
                                  </div>
                                  
                                  <div className="shrink-0 z-10 self-end sm:self-center">
                                    {isCurrentActive && remainingText ? (
                                      <div className="px-3 py-1.5 bg-amber-300 text-slate-950 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-1.5 shadow-md border border-amber-200">
                                        <Timer size={13} className="animate-spin text-slate-900" />
                                        <span>Sisa: {remainingText}</span>
                                      </div>
                                    ) : (
                                      <span className="text-[11px] font-extrabold uppercase tracking-wider px-3 py-1 rounded-xl bg-white/20 text-white border border-white/25">
                                        Waktu Rehat
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            }

                            const agendaEntry = item.data as AgendaEntry;
                            const isAssessable = agendaEntry && [ActivityType.KBM_DAN_QUIZ, ActivityType.SUMATIF, ActivityType.SAS].includes(agendaEntry.activityType || ActivityType.KBM);
                            const gradientClass = activeTheme.cardGradients[idx % activeTheme.cardGradients.length];

                            return (
                                <div 
                                  key={`agenda-item-${agendaEntry.id}-period-${item.startPeriod}-${item.endPeriod}`} 
                                  className={`group relative flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3.5 sm:gap-5 p-4 sm:p-5 rounded-2xl sm:rounded-3xl border transition-all duration-300 overflow-hidden ${gradientClass} ${
                                    isCurrentActive ? activeTheme.activeRing : 'hover:scale-[1.005]'
                                  }`}
                                >
                                    {/* Ambient Glow */}
                                    <div className="absolute -right-10 -bottom-10 w-36 h-36 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>

                                    {/* Left & Center Main Section */}
                                    <div className="flex items-center gap-3.5 sm:gap-4 flex-1 min-w-0 z-10">
                                        {/* Period & Time Capsule */}
                                        <div className={`flex flex-col items-center justify-center w-16 sm:w-20 py-2 sm:py-2.5 rounded-2xl text-center shrink-0 relative overflow-hidden ${activeTheme.capsule}`}>
                                          <span className={`text-[8px] sm:text-[9px] font-black uppercase tracking-wider leading-none mb-0.5 ${activeTheme.capsuleSub}`}>
                                            {item.startPeriod === 0 ? 'TAMBAHAN' : 'JAM KE'}
                                          </span>
                                          <span className="text-lg sm:text-2xl font-black leading-tight">
                                            {item.startPeriod === 0
                                              ? '0'
                                              : (item.startPeriod === item.endPeriod ? item.startPeriod : `${item.startPeriod}-${item.endPeriod}`)}
                                          </span>
                                          {startTimeStr && endTimeStr && (
                                            <span className="text-[8px] sm:text-[10px] font-bold tracking-tight mt-1 pt-1 border-t border-white/20 w-full text-center px-1">
                                              {startTimeStr}-{endTimeStr}
                                            </span>
                                          )}
                                          {isCurrentActive && (
                                            <div className="absolute inset-0 bg-amber-400/20 animate-pulse pointer-events-none" />
                                          )}
                                        </div>
                                    
                                        {/* Details (Class, Subject, Badges, Topic) */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1.5">
                                                {agendaEntry.class && (
                                                  <span className={`px-2.5 py-0.5 rounded-xl text-xs font-black uppercase tracking-tight shrink-0 ${activeTheme.classBadge}`}>
                                                    Kelas {agendaEntry.class}
                                                  </span>
                                                )}
                                                <span className={`text-base sm:text-lg tracking-tight truncate ${activeTheme.subjectColor}`}>
                                                  {agendaEntry.subject}
                                                </span>
                                                {isCurrentActive && (
                                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-black uppercase tracking-wider bg-amber-300 text-slate-950 shadow-md border border-amber-200 shrink-0">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-slate-950 animate-ping"></span>
                                                    Live {remainingText ? `• ${remainingText}` : ''}
                                                  </span>
                                                )}
                                                {agendaEntry.activityType && agendaEntry.activityType !== ActivityType.KBM && (
                                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[9px] sm:text-[10px] font-black uppercase tracking-wider bg-white/20 text-white border border-white/25 shrink-0">
                                                    <PenTool size={10} /> {agendaEntry.activityType}
                                                  </span>
                                                )}
                                            </div>
                                            <p className={`text-xs sm:text-sm font-medium truncate flex items-center gap-2 px-3 py-1.5 rounded-xl ${activeTheme.topicPill}`}>
                                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0"></span>
                                                <span className="font-bold opacity-80">Topik:</span>
                                                <span className="truncate">{agendaEntry.topic || '-'}</span>
                                            </p>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 w-full md:w-auto pt-2.5 md:pt-0 border-t md:border-t-0 border-white/15 shrink-0 z-10">
                                        {isAssessable && (
                                          <button 
                                            onClick={() => onNavigate(View.Grading, { agenda: agendaEntry })}
                                            className={`flex-1 sm:flex-initial h-9 px-3.5 rounded-xl text-xs flex items-center justify-center gap-1.5 transition-all active:scale-95 whitespace-nowrap ${activeTheme.btnGrading}`}
                                          >
                                              <ClipboardList size={14} />
                                              <span>Input Nilai</span>
                                          </button>
                                        )}

                                        <div className="flex-1 sm:flex-initial relative min-w-[125px]">
                                          <select 
                                            value={agendaEntry.status}
                                            onChange={(e) => handleStatusToggle(agendaEntry, e.target.value as any)}
                                            className={`appearance-none w-full h-9 px-3.5 pr-8 rounded-xl text-[10px] font-black uppercase tracking-wider cursor-pointer outline-none transition-all ${activeTheme.statusSelect}`}
                                          >
                                            <option value="Selesai" className="text-slate-900 font-bold">Selesai</option>
                                            <option value="Belum Selesai" className="text-slate-900 font-bold">Belum Selesai</option>
                                            <option value="Jam kosong" className="text-slate-900 font-bold">Jam Kosong</option>
                                          </select>
                                          <ChevronDown size={14} className={`absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none ${activeTheme.statusSelectIcon}`} />
                                        </div>

                                        {agendaEntry.class && !(
                                          agendaEntry?.activityType === ActivityType.SAS || 
                                          agendaEntry?.isAttendanceOptional || 
                                          [ActivityType.KEGIATAN_LOMBA, ActivityType.KEGIATAN_KESISWAAN, ActivityType.KEGIATAN_SEKOLAH].includes(agendaEntry?.activityType as any)
                                        ) && (
                                          <button 
                                            onClick={() => onNavigate(View.Attendance)} 
                                            className={`flex-1 sm:flex-initial h-9 px-4 rounded-xl text-xs flex items-center justify-center gap-1.5 transition-all active:scale-95 whitespace-nowrap ${activeTheme.btnAttendance}`}
                                          >
                                              <UserCheck size={14} />
                                              <span>Absensi</span>
                                          </button>
                                        )}
                                    </div>
                                </div>
                            );
                        }) : (
                            <div className="py-10 text-center border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl bg-slate-50/50 dark:bg-slate-800/20">
                                <Clock className="mx-auto mb-2 text-slate-400 animate-pulse" size={36} />
                                <h4 className="text-xs sm:text-sm font-black text-slate-700 dark:text-slate-300 mb-1">Belum Ada Agenda Hari Ini</h4>
                                <p className="text-xs text-slate-400 font-medium max-w-sm mx-auto mb-3">
                                  Silakan buat agenda harian atau agenda tambahan di menu Agenda Harian.
                                </p>
                                <button
                                  onClick={() => onNavigate(View.Agenda)}
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black text-xs uppercase tracking-wider shadow-sm shadow-indigo-500/20 transition-all active:scale-95"
                                >
                                  <Calendar size={13} />
                                  <span>Buat Agenda Harian</span>
                                </button>
                            </div>
                        )}
                    </div>
                    
                    <button 
                        onClick={() => onNavigate(View.Grading)}
                        className="w-full flex items-center justify-center gap-2.5 px-5 py-3.5 mt-5 bg-indigo-600 hover:bg-indigo-700 text-white font-black rounded-2xl shadow-sm shadow-indigo-500/20 active:scale-98 transition-all uppercase tracking-wider text-xs"
                    >
                        <ClipboardList size={15} />
                        <span>Buka Penilaian</span>
                    </button>

                    {onUpdateTeacher && (
                      <div className="flex flex-col gap-2 w-full mt-3">
                        <button 
                          onClick={() => onUpdateTeacher({ ...activeUser, GradeVisibility: activeUser.GradeVisibility === undefined ? false : !activeUser.GradeVisibility })}
                          className={`w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl border transition-all font-black text-xs uppercase tracking-widest shadow-sm active:scale-95 ${
                            activeUser.GradeVisibility !== false
                              ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-400 hover:bg-emerald-100' 
                              : 'bg-rose-50 border-rose-200 text-rose-700 dark:bg-rose-900/20 dark:border-rose-800 dark:text-rose-400 hover:bg-rose-100'
                          }`}
                        >
                          {activeUser.GradeVisibility !== false ? <Eye size={16} /> : <EyeOff size={16} />}
                          <span>{activeUser.GradeVisibility !== false ? 'Nilai Tugas: Tampil' : 'Nilai Tugas: Sembunyi'}</span>
                        </button>
                        <button 
                          onClick={() => onUpdateTeacher({ ...activeUser, RaporVisibility: activeUser.RaporVisibility === undefined ? false : !activeUser.RaporVisibility })}
                          className={`w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl border transition-all font-black text-xs uppercase tracking-widest shadow-sm active:scale-95 ${
                            activeUser.RaporVisibility !== false
                              ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-400 hover:bg-emerald-100' 
                              : 'bg-rose-50 border-rose-200 text-rose-700 dark:bg-rose-900/20 dark:border-rose-800 dark:text-rose-400 hover:bg-rose-100'
                          }`}
                        >
                          {activeUser.RaporVisibility !== false ? <Eye size={16} /> : <EyeOff size={16} />}
                          <span>{activeUser.RaporVisibility !== false ? 'Nilai Rapor: Tampil' : 'Nilai Rapor: Sembunyi'}</span>
                        </button>
                        <button 
                          onClick={() => onUpdateTeacher({ ...activeUser, SasVisibility: activeUser.SasVisibility === undefined ? false : !activeUser.SasVisibility })}
                          className={`w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl border transition-all font-black text-xs uppercase tracking-widest shadow-sm active:scale-95 ${
                            activeUser.SasVisibility !== false
                              ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-400 hover:bg-emerald-100' 
                              : 'bg-rose-50 border-rose-200 text-rose-700 dark:bg-rose-900/20 dark:border-rose-800 dark:text-rose-400 hover:bg-rose-100'
                          }`}
                        >
                          {activeUser.SasVisibility !== false ? <Eye size={16} /> : <EyeOff size={16} />}
                          <span>{activeUser.SasVisibility !== false ? 'Nilai S. Akhir: Tampil' : 'Nilai S. Akhir: Sembunyi'}</span>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">
              {stats.map((stat, idx) => (
                <StatCard key={`stat-${stat.title}-${idx}`} {...stat} />
              ))}
              <div className="col-span-2 lg:col-span-2 bg-indigo-500 dark:bg-indigo-900 p-6 sm:p-8 rounded-3xl sm:rounded-[2.5rem] shadow-sm border border-indigo-600 dark:border-indigo-800">
                <h3 className="text-lg font-black text-white mb-6">Cari Siswa</h3>
                <div className="relative mb-4">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-indigo-200" size={18} />
                  <input 
                    type="text" 
                    placeholder="Cari Nama atau NISN..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-2xl text-white placeholder:text-indigo-200 font-bold focus:ring-4 focus:ring-white/10 outline-none transition-all"
                  />
                </div>
                <div className="space-y-2">
                  {searchResults.length > 0 ? (
                    searchResults.map((student, idx) => (
                      <div 
                        key={`search-${student.id}-${idx}`} 
                        onClick={() => onNavigate(View.StudentProfile, { student })}
                        className="flex items-center gap-4 p-3 bg-white/10 hover:bg-white/20 rounded-2xl border border-white/10 cursor-pointer transition-all group"
                      >
                        <div className="w-10 h-10 bg-indigo-400/20 rounded-xl flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                          <User size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-black text-white truncate">{student.name}</p>
                          <div className="flex gap-2 items-center">
                            <span className="text-[10px] font-bold text-indigo-100">NISN: {student.nisn || student.nis}</span>
                            <span className="w-1 h-1 bg-white/30 rounded-full"></span>
                            <span className="text-[10px] font-bold text-indigo-100">{student.class}</span>
                            <span className="w-1 h-1 bg-white/30 rounded-full"></span>
                            <span className="text-[10px] font-bold text-indigo-100">{student.gender === 'Laki-laki' ? 'L' : 'P'}</span>
                          </div>
                        </div>
                        <ArrowRight size={16} className="text-white/50 group-hover:translate-x-1 transition-transform" />
                      </div>
                    ))
                  ) : searchQuery.trim() ? (
                    <p className="text-center py-4 text-xs font-bold text-indigo-100 italic">Siswa tidak ditemukan</p>
                  ) : (
                    <p className="text-center py-4 text-xs font-bold text-indigo-100 italic">Masukkan nama atau NISN untuk mencari</p>
                  )}
                </div>
              </div>
              <div className="col-span-2 lg:col-span-2 bg-rose-500 dark:bg-rose-900 p-6 sm:p-8 rounded-3xl sm:rounded-[2.5rem] shadow-sm border border-rose-600 dark:border-rose-800">
                <h3 className="text-lg font-black text-white mb-6">Agendas in progress</h3>
                <div className="space-y-4">
                  {agendasInProgress.length > 0 ? (
                    agendasInProgress.slice(0, 3).map((agenda, idx) => (
                      <div key={`progress-${agenda.id}-${idx}`} className="bg-white/10 p-4 rounded-2xl border border-white/10 hover:bg-white/20 transition-all cursor-pointer" onClick={() => onNavigate(View.Agenda, { agenda })}>
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-[10px] font-black uppercase tracking-widest text-rose-100">{agenda.class}</span>
                          <span className="text-[10px] font-bold text-rose-200">{agenda.date}</span>
                        </div>
                        <p className="text-sm font-bold text-white line-clamp-1">{agenda.topic}</p>
                        {agenda.notes && (
                          <p className="text-[10px] text-rose-100 mt-1 italic line-clamp-1">{agenda.notes}</p>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="py-8 text-center">
                      <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
                        <CheckCircle className="text-rose-200" size={24} />
                      </div>
                      <p className="text-xs text-rose-100 font-bold italic">Semua agenda telah selesai! ✨</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
        </div>

        <div className="flex flex-col gap-6 sm:gap-8">
          <div className="bg-gradient-to-br from-blue-500 to-pink-500 dark:from-slate-800 dark:to-slate-900 p-6 sm:p-8 rounded-3xl sm:rounded-[2.5rem] shadow-2xl shadow-blue-500/20 text-white relative overflow-hidden group">
              <div className="absolute -bottom-10 -right-10 w-32 h-32 sm:w-40 sm:h-40 bg-white/10 rounded-full group-hover:scale-125 transition-transform duration-700"></div>
              <h3 className="text-lg sm:text-xl font-black mb-4 sm:mb-6 tracking-tight relative z-10 text-white">Informasi Sekolah</h3>
              <ul className="space-y-4 sm:space-y-5 relative z-10">
                  {visibleAnnouncements.length > 0 ? (
                    visibleAnnouncements.map((ann: any, idx) => (
                      <li key={`ann-${ann.id || idx}`} className="flex gap-3 sm:gap-4 group/ann">
                          <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full mt-1.5 sm:mt-2 shrink-0 ${
                            ann.type === 'urgent' ? 'bg-rose-400' : 
                            ann.type === 'success' ? 'bg-emerald-400' : 'bg-cyan-400 animate-pulse'
                          }`}></div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs sm:text-sm leading-relaxed ${
                              ann.type === 'urgent' ? 'text-white font-black italic' : 'text-blue-50 font-medium'
                            }`}>
                              {ann.text}
                            </p>
                          </div>
                          <div className="flex gap-1 opacity-0 group-hover/ann:opacity-100 transition-opacity">
                            <button 
                              onClick={() => handleDismissAnnouncement(ann.id)}
                              title="Sembunyikan"
                              className="p-1 hover:bg-white/20 rounded text-white/70 hover:text-white transition-colors"
                            >
                              <EyeOff size={14} />
                            </button>
                            {role === UserRole.Admin && (
                              <button 
                                onClick={() => handleDeleteAnnouncement(ann.id)}
                                title="Hapus Permanen"
                                className="p-1 hover:bg-rose-500/30 rounded text-rose-200 hover:text-rose-100 transition-colors"
                              >
                                <X size={14} />
                              </button>
                            )}
                          </div>
                      </li>
                    ))
                  ) : (
                    <li className="flex gap-3 sm:gap-4">
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-400 rounded-full mt-1.5 sm:mt-2 shrink-0"></div>
                      <p className="text-xs sm:text-sm text-blue-200 font-medium italic">
                        {schoolSettings.announcements?.length > 0 ? 'Semua pengumuman telah dibaca.' : 'Belum ada pengumuman baru.'}
                      </p>
                    </li>
                  )}
              </ul>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-pink-500 dark:from-slate-800 dark:to-slate-900 p-6 sm:p-8 rounded-3xl sm:rounded-[2.5rem] shadow-sm border border-blue-600 dark:border-blue-800">
             <h4 className="text-[9px] sm:text-[10px] font-black text-white uppercase tracking-widest mb-4 sm:mb-6">Pintasan Fitur</h4>
             <div className="grid grid-cols-2 lg:grid-cols-1 gap-3 sm:gap-4">
                <button onClick={() => onNavigate(View.MaterialGenerator)} className="p-4 sm:p-5 bg-slate-50 dark:bg-slate-950/40 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 hover:bg-indigo-600 hover:text-white transition-all group border border-slate-100 dark:border-slate-800">
                    <div className="p-2.5 sm:p-3 bg-white dark:bg-slate-900 rounded-xl group-hover:bg-indigo-500 transition-colors">
                        <Sparkles size={18} className="text-indigo-600 group-hover:text-white sm:w-5 sm:h-5" />
                    </div>
                    <div className="text-left">
                        <span className="text-[10px] sm:text-xs font-black uppercase block tracking-widest leading-tight">Buat Materi</span>
                        <span className="text-[9px] sm:text-[10px] text-slate-400 group-hover:text-indigo-100 font-medium">Modul Ajar AI</span>
                    </div>
                    <ArrowRight size={16} className="hidden sm:block ml-auto opacity-0 group-hover:opacity-100 transition-all" />
                </button>
                <button onClick={() => onNavigate(View.LearningObjectives)} className="p-4 sm:p-5 bg-slate-50 dark:bg-slate-950/40 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 hover:bg-emerald-600 hover:text-white transition-all group border border-slate-100 dark:border-slate-800">
                    <div className="p-2.5 sm:p-3 bg-white dark:bg-slate-900 rounded-xl group-hover:bg-emerald-500 transition-colors">
                        <TrendingUp size={18} className="text-emerald-600 group-hover:text-white sm:w-5 sm:h-5" />
                    </div>
                    <div className="text-left">
                        <span className="text-[10px] sm:text-xs font-black uppercase block tracking-widest leading-tight">Target Capaian</span>
                        <span className="text-[9px] sm:text-[10px] text-slate-400 group-hover:text-emerald-100 font-medium">Tujuan Belajar</span>
                    </div>
                    <ArrowRight size={16} className="hidden sm:block ml-auto opacity-0 group-hover:opacity-100 transition-all" />
                </button>
                
                {role === UserRole.Guru && (
                  <>
                    <button 
                      onClick={() => {
                        const printWindow = window.open('', '_blank');
                        if (!printWindow) return;
                        
                        const html = `
                          <html>
                            <head>
                              <title>Cover Administrasi - ${activeUser.name}</title>
                              <style>
                                @page { size: A4; margin: 0; }
                                * { -webkit-print-color-adjust: exact; }
                                body { 
                                  font-family: 'Times New Roman', Times, serif; 
                                  margin: 0; 
                                  padding: 1cm 2cm;
                                  height: 285mm;
                                  box-sizing: border-box;
                                  display: flex;
                                  flex-direction: column;
                                  align-items: center;
                                  justify-content: space-between;
                                  text-align: center;
                                  color: #000;
                                  position: relative;
                                  overflow: hidden;
                                }
                                .bold { font-weight: bold; }
                                .kop { width: 100%; padding-top: 10px; margin-top: 5px; }
                                .kop-title { font-size: 18px; font-weight: bold; text-transform: uppercase; margin: 0; }
                                .kop-school { font-size: 24px; font-weight: bold; text-transform: uppercase; margin: 2px 0; }
                                .kop-address { font-size: 12px; font-style: italic; }
                                
                                .main-title { margin-top: 20px; letter-spacing: 2px; }
                                .doc-title { font-size: 36px; font-weight: bold; text-transform: uppercase; margin-bottom: 0; }
                                .doc-list { 
                                  font-size: 18px; 
                                  font-weight: bold; 
                                  text-transform: uppercase; 
                                  margin: 20px 0; 
                                  line-height: 1.4; 
                                  display: inline-block; 
                                  text-align: left;
                                }
                                
                                .teacher-info {
                                  width: 100%;
                                  margin: 20px 0;
                                }
                                .info-table { border-collapse: collapse; font-size: 18px; margin: 0 auto; width: 85%; }
                                .info-table td { padding: 4px 5px; text-align: left; }
                                .info-table td:first-child { width: 40%; font-weight: bold; }
                                .info-table td:nth-child(2) { width: 5%; text-align: center; }
                                .info-table td:last-child { width: 55%; }
                                .bold { font-weight: bold; }
                              </style>
                            </head>
                            <body>
                              <div class="main-title">
                                <div class="doc-title">Administrasi Guru</div>
                              </div>

                              <div style="text-align: center; margin: 15px 0;">
                                <img src="${schoolSettings.logoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9c/Logo_Kemdikbud.png/800px-Logo_Kemdikbud.png'}" style="width: 140px; height: 140px; object-fit: contain; image-rendering: high-quality; image-rendering: -webkit-optimize-contrast;" referrerPolicy="no-referrer" />
                              </div>

                              <div style="text-align: center;">
                                <div class="doc-list">
                                  <div>- Tujuan Pembelajaran</div>
                                  <div>- Agenda Harian</div>
                                  <div>- Absensi Siswa</div>
                                  <div>- Rekapitulasi Nilai Siswa</div>
                                </div>
                              </div>

                              <div class="teacher-info">
                                <table class="info-table">
                                  <tr>
                                    <td>Nama Guru</td>
                                    <td>:</td>
                                    <td class="bold" style="text-transform: uppercase; white-space: nowrap; font-size: 16px;">${activeUser.name}</td>
                                  </tr>
                                  ${activeUser.nip ? `
                                  <tr>
                                    <td>NIP/NUPTK</td>
                                    <td>:</td>
                                    <td>${activeUser.nip}</td>
                                  </tr>` : ''}
                                  <tr>
                                    <td>Mata Pelajaran</td>
                                    <td>:</td>
                                    <td class="bold">${activeUser.subject || '-'}</td>
                                  </tr>
                                  <tr>
                                    <td>Semester</td>
                                    <td>:</td>
                                    <td class="bold">${schoolSettings.semester || '-'}</td>
                                  </tr>
                                  <tr>
                                    <td>Tahun Pelajaran</td>
                                    <td>:</td>
                                    <td class="bold">${schoolSettings.academicYear || (new Date().getFullYear() + '/' + (new Date().getFullYear() + 1))}</td>
                                  </tr>
                                </table>
                              </div>

                              <div class="kop">
                                <div class="kop-title">Pemerintah Kabupaten ${schoolSettings.kabupaten || 'Ciamis'}</div>
                                <div class="kop-title">Dinas Pendidikan</div>
                                <div class="kop-school">${schoolSettings.schoolName || 'SMP NEGERI 1 KAWALI'}</div>
                                <div class="kop-address">${schoolSettings.address || 'Jl. Veteran No. 79 Kawali - Ciamis'}</div>
                              </div>
                              
                              <script>
                                window.onload = () => { window.print(); setTimeout(() => window.close(), 500); };
                              </script>
                            </body>
                          </html>
                        `;
                        
                        printWindow.document.write(html);
                        printWindow.document.close();
                      }}
                      className="p-4 sm:p-5 bg-slate-50 dark:bg-slate-950/40 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 hover:bg-amber-600 hover:text-white transition-all group border border-slate-100 dark:border-slate-800"
                    >
                        <div className="p-2.5 sm:p-3 bg-white dark:bg-slate-900 rounded-xl group-hover:bg-amber-500 transition-colors">
                            <PenTool size={18} className="text-amber-600 group-hover:text-white sm:w-5 sm:h-5" />
                        </div>
                        <div className="text-left">
                            <span className="text-[10px] sm:text-xs font-black uppercase block tracking-widest leading-tight">Cetak Cover</span>
                            <span className="text-[9px] sm:text-[10px] text-slate-400 group-hover:text-amber-100 font-medium">Jilid Administrasi</span>
                        </div>
                        <ArrowRight size={16} className="hidden sm:block ml-auto opacity-0 group-hover:opacity-100 transition-all" />
                     </button>

                    <button 
                      onClick={() => {
                        const printWindow = window.open('', '_blank');
                        if (!printWindow) return;
                        
                        const html = `
                          <html>
                            <head>
                              <title>Peraturan Pemerintah - ${activeUser.name}</title>
                              <style>
                                @page { size: A4; margin: 2cm; }
                                * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                                body { 
                                  font-family: 'Times New Roman', Times, serif; 
                                  color: #000; 
                                  line-height: 1.5; 
                                  font-size: 13px; 
                                  margin: 0; 
                                  padding: 0; 
                                }
                                .page { 
                                  page-break-after: always; 
                                  box-sizing: border-box; 
                                }
                                .page:last-child { 
                                  page-break-after: avoid; 
                                }
                                .header-title { 
                                  font-size: 16px; 
                                  font-weight: bold; 
                                  text-align: center; 
                                  text-transform: uppercase; 
                                  margin-bottom: 5px; 
                                }
                                .header-subtitle { 
                                  font-size: 13px; 
                                  text-align: center; 
                                  margin-bottom: 25px; 
                                  font-weight: bold; 
                                }
                                table.regulation { 
                                  width: 100%; 
                                  border-collapse: collapse; 
                                  margin-top: 20px; 
                                }
                                table.regulation th, table.regulation td { 
                                  border: 1px solid black; 
                                  padding: 8px 10px; 
                                  text-align: left; 
                                  vertical-align: top; 
                                }
                                table.regulation th { 
                                  background-color: #cbd5e1; 
                                  font-weight: bold; 
                                  text-align: center; 
                                }
                                table.regulation td.num { 
                                  text-align: center; 
                                  width: 30px; 
                                }
                                table.regulation td.doc { 
                                  font-weight: bold; 
                                }
                                .section-title { 
                                  font-size: 13px; 
                                  font-weight: bold; 
                                  text-transform: uppercase; 
                                  margin-top: 20px; 
                                  margin-bottom: 10px; 
                                  border-bottom: 2px solid black; 
                                  padding-bottom: 3px; 
                                }
                                .subtitle-bold { 
                                  font-weight: bold; 
                                  margin-top: 8px; 
                                  margin-bottom: 4px; 
                                }
                                ul, ol { 
                                  margin: 3px 0 10px 20px; 
                                  padding-left: 0; 
                                }
                                li { 
                                  margin-bottom: 4px; 
                                  text-align: justify; 
                                }
                              </style>
                            </head>
                            <body>
                              <!-- PAGE 1 -->
                              <div class="page">
                                <div class="header-title">DAFTAR ATURAN DAN PERATURAN PEMERINTAH</div>
                                <div class="header-subtitle">Administrasi Guru, Penilaian, dan Absensi Siswa SMP / Fase D</div>
                                
                                <table class="regulation">
                                  <thead>
                                    <tr>
                                      <th style="width: 5%;">No</th>
                                      <th style="width: 45%;">Peraturan / Dokumen</th>
                                      <th style="width: 50%;">Penjelasan Singkat</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td class="num">1</td>
                                      <td class="doc">Permendikbudristek No. 21 Tahun 2022 tentang Standar Penilaian Pendidikan</td>
                                      <td>Mengatur asesmen formatif dan sumatif, pengolahan nilai, kenaikan kelas, dan kelulusan peserta didik.</td>
                                    </tr>
                                    <tr>
                                      <td class="num">2</td>
                                      <td class="doc">PP No. 57 Tahun 2021 tentang Standar Nasional Pendidikan</td>
                                      <td>Menjadi dasar pelaksanaan standar proses, pembelajaran, dan penilaian pendidikan nasional.</td>
                                    </tr>
                                    <tr>
                                      <td class="num">3</td>
                                      <td class="doc">Panduan Pembelajaran dan Asesmen Kurikulum Merdeka</td>
                                      <td>Pedoman teknis pelaksanaan asesmen, proyek, praktik, dan pengolahan hasil belajar Kurikulum Merdeka.</td>
                                    </tr>
                                    <tr>
                                      <td class="num">4</td>
                                      <td class="doc">Permendikbudristek No. 16 Tahun 2022 tentang Standar Proses</td>
                                      <td>Mengatur perencanaan pembelajaran, modul ajar, asesmen, dan administrasi guru.</td>
                                    </tr>
                                    <tr>
                                      <td class="num">5</td>
                                      <td class="doc">Permendikbud No. 23 Tahun 2015 tentang Penumbuhan Budi Pekerti</td>
                                      <td>Menjadi dasar pembiasaan disiplin, karakter, dan kehadiran siswa di sekolah.</td>
                                    </tr>
                                    <tr>
                                      <td class="num">6</td>
                                      <td class="doc">Permendikbud No. 10 Tahun 2020 tentang Program Indonesia Pintar</td>
                                      <td>Kehadiran siswa menjadi salah satu indikator keaktifan peserta didik dalam pendidikan.</td>
                                    </tr>
                                    <tr>
                                      <td class="num">7</td>
                                      <td class="doc">Kurikulum Operasional Satuan Pendidikan (KOSP)</td>
                                      <td>Mengatur pelaksanaan pembelajaran, penilaian, dan tata tertib sesuai kebijakan sekolah.</td>
                                    </tr>
                                    <tr>
                                      <td class="num">8</td>
                                      <td class="doc">Tata Tertib Sekolah SMP</td>
                                      <td>Mengatur absensi, izin, sakit, alfa, keterlambatan, dan disiplin siswa di sekolah.</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>

                              <!-- PAGE 2 -->
                              <div class="page">
                                <div class="section-title">KETENTUAN PENILAIAN</div>
                                <div class="subtitle-bold">Mengacu pada:</div>
                                <ol>
                                  <li>Permendikbudristek No. 21 Tahun 2022 tentang Standar Penilaian Pendidikan.</li>
                                  <li>Panduan Pembelajaran dan Asesmen Kurikulum Merdeka.</li>
                                </ol>
                                <div class="subtitle-bold">Isi aturannya menjelaskan bahwa:</div>
                                <ol>
                                  <li>Penilaian terdiri dari asesmen formatif dan sumatif.</li>
                                  <li>Guru/satuan pendidikan diberi kewenangan mengolah penilaian.</li>
                                  <li>Nilai praktikum adalah nilai yang diperoleh peserta didik dari kegiatan praktik atau penerapan langsung suatu keterampilan untuk menilai kemampuan nyata dalam melakukan suatu tugas sesuai tujuan pembelajaran.</li>
                                  <li>Pembobotan presentase nilai diserahkan kepada guru, MGMP, atau kebijakan satuan pendidikan sesuai karakter mata pelajaran.</li>
                                </ol>

                                <div class="section-title" style="margin-top: 25px;">KETENTUAN ABSENSI SISWA SMP / FASE D</div>
                                <div class="subtitle-bold">Mengacu pada:</div>
                                <ul>
                                  <li>Permendikbud No. 23 Tahun 2015 tentang Penumbuhan Budi Pekerti.</li>
                                  <li>Tata Tertib Sekolah dan KOSP masing-masing satuan pendidikan.</li>
                                </ul>
                                <div class="subtitle-bold">Ketentuan umum:</div>
                                <ol>
                                  <li>Kehadiran siswa menjadi bagian dari pembinaan disiplin dan karakter peserta didik.</li>
                                  <li>Absensi siswa terdiri dari:
                                    <ul style="list-style-type: circle; margin: 3px 0 3px 20px;">
                                      <li>Hadir</li>
                                      <li>Izin</li>
                                      <li>Sakit</li>
                                      <li>Alfa/Tanpa Keterangan</li>
                                    </ul>
                                  </li>
                                  <li>Siswa yang tidak hadir wajib memberikan keterangan atau surat izin dari orang tua/wali.</li>
                                  <li>Ketidakhadiran tanpa keterangan (alfa) dapat memengaruhi pembinaan disiplin dan tindak lanjut sekolah.</li>
                                  <li>Rekap absensi dicatat oleh wali kelas dan menjadi bagian administrasi sekolah serta laporan hasil belajar siswa.</li>
                                </ol>

                                <div class="section-title" style="margin-top: 25px;">KETENTUAN AGENDA HARIAN MENGAJAR</div>
                                <div class="subtitle-bold">Mengacu pada:</div>
                                <ul>
                                  <li>Permendikbudristek No. 16 Tahun 2022 tentang Standar Proses.</li>
                                  <li>Administrasi Pembelajaran pada Kurikulum Merdeka.</li>
                                </ul>
                                <div class="subtitle-bold">Ketentuan umum:</div>
                                <ol>
                                  <li>Guru wajib mencatat kegiatan pembelajaran yang dilaksanakan setiap pertemuan.</li>
                                  <li>Agenda harian mengajar digunakan sebagai bukti administrasi pelaksanaan pembelajaran.</li>
                                  <li>Isi agenda harian mengajar umumnya memuat:
                                    <ul style="list-style-type: circle; margin: 3px 0 3px 20px;">
                                      <li>Hari dan tanggal</li>
                                      <li>Kelas</li>
                                      <li>Materi pembelajaran</li>
                                      <li>Tujuan/kegiatan pembelajaran</li>
                                      <li>Kehadiran siswa</li>
                                      <li>Penilaian atau tindak lanjut pembelajaran</li>
                                    </ul>
                                  </li>
                                  <li>Agenda harian mengajar menjadi bagian dari administrasi guru dan supervisi akademik sekolah.</li>
                                  <li>Bentuk agenda harian dapat disesuaikan dengan kebijakan satuan pendidikan.</li>
                                </ol>

                                <div class="section-title" style="margin-top: 25px;">KETENTUAN PROGRAM KOKURIKULER<br/>&ldquo;7 KEBIASAAN ANAK INDONESIA HEBAT&rdquo;</div>
                                <div class="subtitle-bold">Mengacu pada:</div>
                                <ol>
                                  <li>Kurikulum Merdeka.</li>
                                  <li>Profil Pelajar Pancasila.</li>
                                  <li>Penguatan Pendidikan Karakter (PPK).</li>
                                  <li>KOSP Satuan Pendidikan.</li>
                                </ol>
                                <div class="subtitle-bold">Ketentuan Umum:</div>
                                <ol>
                                  <li>Program kokurikuler dilaksanakan sebagai pembiasaan positif peserta didik.</li>
                                  <li>Kegiatan bertujuan membentuk karakter disiplin, mandiri, sehat, dan bertanggung jawab.</li>
                                  <li>Pelaksanaan program terintegrasi dalam kegiatan sekolah dan pembelajaran.</li>
                                  <li>Pembinaan dilakukan oleh wali kelas, guru mata pelajaran, BK, dan sekolah.</li>
                                  <li>Monitoring kegiatan dapat dilakukan melalui observasi, jurnal, atau pembiasaan harian siswa.</li>
                                  <li>Program meliputi:
                                    <ul style="list-style-type: circle; margin: 3px 0 3px 20px;">
                                      <li>Bangun pagi</li>
                                      <li>Beribadah</li>
                                      <li>Berolahraga</li>
                                      <li>Makan sehat dan bergizi</li>
                                      <li>Gemar belajar</li>
                                      <li>Bermasyarakat</li>
                                      <li>Tidur cepat</li>
                                    </ul>
                                  </li>
                                  <li>Pelaksanaan program disesuaikan dengan kondisi dan kebijakan satuan pendidikan.</li>
                                </ol>
                              </div>

                              <script>
                                window.onload = () => { window.print(); setTimeout(() => window.close(), 500); };
                              </script>
                            </body>
                          </html>
                        `;
                        
                        printWindow.document.write(html);
                        printWindow.document.close();
                      }}
                      className="p-4 sm:p-5 bg-slate-50 dark:bg-slate-950/40 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 hover:bg-emerald-600 hover:text-white transition-all group border border-slate-100 dark:border-slate-800"
                    >
                        <div className="p-2.5 sm:p-3 bg-white dark:bg-slate-900 rounded-xl group-hover:bg-emerald-500 transition-colors">
                            <ShieldCheck size={18} className="text-emerald-600 group-hover:text-white sm:w-5 sm:h-5" />
                        </div>
                        <div className="text-left">
                            <span className="text-[10px] sm:text-xs font-black uppercase block tracking-widest leading-tight">Cetak Peraturan Pemerintah</span>
                            <span className="text-[9px] sm:text-[10px] text-slate-400 group-hover:text-emerald-100 font-medium">Aturan &amp; Ketentuan</span>
                        </div>
                        <ArrowRight size={16} className="hidden sm:block ml-auto opacity-0 group-hover:opacity-100 transition-all" />
                    </button>

                    <button 
                      onClick={() => {
                        const printWindow = window.open('', '_blank');
                        if (!printWindow) return;
                        
                        const html = `
                          <html>
                            <head>
                              <title>Lembar Pengesahan - ${activeUser.name}</title>
                              <style>
                                @page { size: A4; margin: 2.5cm; }
                                body { font-family: 'Times New Roman', Times, serif; color: #000; line-height: 1.8; }
                                .title { 
                                  text-align: center; 
                                  font-size: 22px; 
                                  font-weight: bold; 
                                  text-decoration: underline; 
                                  text-transform: uppercase; 
                                  margin: 0 0 50px 0; 
                                  letter-spacing: 1px;
                                }
                                .content { margin: 20px auto; width: 100%; font-size: 16px; }
                                .signature-table { width: 100%; margin-top: 120px; border-collapse: collapse; }
                                .signature-cell { width: 50%; vertical-align: top; text-align: center; }
                                .signature-space { height: 100px; }
                                .bold { font-weight: bold; }
                                .underline { text-decoration: underline; }
                                .info-row { display: flex; margin-bottom: 4px; font-size: 16px; }
                                .info-label { width: 180px; font-weight: bold; }
                                .info-sep { width: 20px; }
                              </style>
                            </head>
                            <body>
                              <div class="title" style="margin-bottom: 40px;">Lembar Pengesahan</div>

                              <div class="content">
                                <div style="margin-bottom: 30px;">
                                  <div class="info-row"><div class="info-label">Satuan Pendidikan</div><div class="info-sep">:</div><div class="bold">${schoolSettings.schoolName || 'SMP NEGERI 1 KAWALI'}</div></div>
                                  <div class="info-row"><div class="info-label">Mata Pelajaran</div><div class="info-sep">:</div><div class="bold">${activeUser.subject || '-'}</div></div>
                                  <div class="info-row"><div class="info-label">Tahun Pelajaran</div><div class="info-sep">:</div><div class="bold">${schoolSettings.academicYear || (new Date().getFullYear() + '/' + (new Date().getFullYear() + 1))}</div></div>
                                  <div class="info-row"><div class="info-label">Semester</div><div class="info-sep">:</div><div class="bold">${schoolSettings.semester || '-'}</div></div>
                                  <div class="info-row"><div class="info-label">Nama Guru</div><div class="info-sep">:</div><div class="bold">${activeUser.name}</div></div>
                                  ${activeUser.nip ? `<div class="info-row"><div class="info-label">NIP/NUPTK</div><div class="info-sep">:</div><div>${activeUser.nip}</div></div>` : ''}
                                </div>

                                <p style="text-align: justify; margin-top: 60px;">
                                  Demikian lembar pengesahan ini dibuat untuk dapat dipergunakan sebagaimana mestinya.
                                </p>
                              </div>

                              <table class="signature-table">
                                <tr>
                                  <td class="signature-cell">
                                    <p>Mengetahui,</p>
                                    <p>Kepala ${schoolSettings.schoolName || 'SMP NEGERI 1 KAWALI'}</p>
                                    <div class="signature-space"></div>
                                    <p class="bold underline" style="font-size: 18px;">${schoolSettings.principalName || 'NAMA KEPALA SEKOLAH'}</p>
                                    <div style="font-size: 15px; margin-top: 5px;">NIP. ${schoolSettings.principalNip || '...........................................'}</div>
                                  </td>
                                  <td class="signature-cell">
                                    <p>${schoolSettings.kecamatan || 'Kawali'}, ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                                    <p>Guru Mata Pelajaran,</p>
                                    <div class="signature-space"></div>
                                    <p class="bold underline" style="font-size: 18px;">${activeUser.name}</p>
                                    ${activeUser.nip ? `<div style="font-size: 15px; margin-top: 5px;">NIP. ${activeUser.nip}</div>` : ''}
                                  </td>
                                </tr>
                              </table>

                              <script>
                                window.onload = () => { window.print(); setTimeout(() => window.close(), 500); };
                              </script>
                            </body>
                          </html>
                        `;
                        
                        printWindow.document.write(html);
                        printWindow.document.close();
                      }}
                      className="p-4 sm:p-5 bg-slate-50 dark:bg-slate-950/40 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 hover:bg-indigo-600 hover:text-white transition-all group border border-slate-100 dark:border-slate-800"
                    >
                        <div className="p-2.5 sm:p-3 bg-white dark:bg-slate-900 rounded-xl group-hover:bg-indigo-500 transition-colors">
                            <FileDown size={18} className="text-indigo-600 group-hover:text-white sm:w-5 sm:h-5" />
                        </div>
                        <div className="text-left">
                            <span className="text-[10px] sm:text-xs font-black uppercase block tracking-widest leading-tight">Pengesahan</span>
                            <span className="text-[9px] sm:text-[10px] text-slate-400 group-hover:text-indigo-100 font-medium">Lembar Verifikasi</span>
                        </div>
                        <ArrowRight size={16} className="hidden sm:block ml-auto opacity-0 group-hover:opacity-100 transition-all" />
                    </button>
                  </>
                )}
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardView;
